import { SourceSystem } from 'src/utils/enums/financialTypes.enum';
import { TERMDATE_ITEM_NAME } from 'src/config';

const ZERO = 0;

export function filterFinanceTypes(
    make: string,
    vehicleType: string,
    brand: string,
    country: string,
    year: number,
    stateProvince: string,
) {
    const allUpperMake = make.toUpperCase();
    const rules = new FinanceInformationRule();

    const financeTypes = rules
        .getRules()
        .filter(
            (rule) =>
                rule.VehicleType === vehicleType &&
                rule.Brand === brand &&
                rule.Country === country &&
                ((rule.MakesList.length > ZERO &&
                    rule.MakesList.some(
                        (m) => m.toUpperCase() === allUpperMake,
                    )) ||
                    (rule.NotMakesList.length > ZERO &&
                        !rule.NotMakesList.some(
                            (m) => m.toUpperCase() === allUpperMake,
                        ))) &&
                rule.RelativeYears.some(
                    (ruleYear) => getTermDate() - parseInt(ruleYear) === year,
                ) &&
                (rule.StatesList.length <= ZERO ||
                    rule.StatesList.includes(stateProvince)),
        )
        .map((rule) => rule.FinanceType)
        .filter((value, index, self) => self.indexOf(value) === index);

    const financeTypeList = financeTypes.map((financeType) => ({
        value: financeType,
    }));

    return financeTypeList;
}

class FinanceInformationRule {
    private rules: Array<IRule> = [];
    private emptyArray: Array<string> = [];

    private createArray(params: string): Array<string> {
        return params.split(' ');
    }

    private getStatesList(id?: string): Array<string> {
        const stateRules = CONSTANTS.financialRules.statesRules.stateRule;

        if (id) {
            for (const rule of stateRules) {
                if (rule._id === id) {
                    return this.createArray(rule._states);
                }
            }
        }

        return this.emptyArray;
    }

    constructor() {
        for (const rule of CONSTANTS.financialRules.rules.rule) {
            const rl: IRule = {
                VehicleType: rule._vehicleType,
                Brand: rule._brand,
                Country: rule._country,
                FinanceType: rule._product,
                RelativeYears: this.createArray(rule._relativeYears),
                MakesList: rule._makein
                    ? this.createArray(rule._makein)
                    : this.emptyArray,
                NotMakesList: rule._makenotin
                    ? this.createArray(rule._makenotin)
                    : this.emptyArray,
                StatesList: this.getStatesList(rule._stateRuleId),
            };

            this.rules.push(rl);
        }
    }

    getRules() {
        return this.rules;
    }
}

interface IRule {
    VehicleType: string;
    Brand: string;
    Country: string;
    MakesList: Array<String>;
    NotMakesList: Array<string>;
    RelativeYears: Array<string>;
    FinanceType: string;
    StatesList: Array<string>;
}

const CONSTANTS = {
    financialRules: {
        rules: {
            rule: [
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Loan',
                    _vehicleType: 'New',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Loan',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8 9',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makenotin: 'VOLKSWAGEN',
                    _product: 'Loan',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8 9',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Loan',
                    _vehicleType: 'CPO',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makenotin: 'VOLKSWAGEN',
                    _product: 'Loan',
                    _vehicleType: 'CPO',
                    _relativeYears: '-1 0 1 2 3 4 5',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Loan',
                    _vehicleType: 'Demo',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Lease',
                    _vehicleType: 'New',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Lease',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'AUDI',
                    _product: 'Lease',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Balloon',
                    _vehicleType: 'CPO',
                    _relativeYears: '-1 0 1 2 3 4 5',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Lease',
                    _vehicleType: 'Demo',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Balloon',
                    _vehicleType: 'Demo',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4 5',
                    _stateRuleId: 'CanadaVwAudi',
                },
                {
                    _country: 'Canada',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Balloon',
                    _vehicleType: 'New',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                    _stateRuleId: 'CanadaVwAudi',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Loan',
                    _vehicleType: 'New',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Loan',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8 9',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makenotin: 'AUDI',
                    _product: 'Loan',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8 9',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Loan',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Loan',
                    _vehicleType: 'CPO',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Lease',
                    _vehicleType: 'New',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Lease',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1,0 1 2 3 4 5 6 7',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Lease',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Lease',
                    _vehicleType: 'CPO',
                    _relativeYears: '-1 0 1 2 3 4',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Lease',
                    _vehicleType: 'Demo',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Loan',
                    _vehicleType: 'Demo',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                },

                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Balloon',
                    _vehicleType: 'New',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                    _stateRuleId: 'CanadaVwAudi',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Balloon',
                    _vehicleType: 'Demo',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3 4',
                    _stateRuleId: 'CanadaVwAudi',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Balloon',
                    _vehicleType: 'CPO',
                    _relativeYears: '0 1 2 3 4 5',
                    _stateRuleId: 'CanadaVwAudi',
                },
                {
                    _country: 'Canada',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Balloon',
                    _vehicleType: 'CPO',
                    _relativeYears: '-1 0 1 2 3 4 5',
                    _stateRuleId: 'CanadaVwAudi',
                },
                {
                    _country: 'USA',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Lease',
                    _vehicleType: 'New',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3',
                },
                {
                    _country: 'USA',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Lease',
                    _vehicleType: 'CPO',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8',
                },
                {
                    _country: 'USA',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Lease',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8',
                },
                {
                    _country: 'USA',
                    _brand: 'Audi',
                    _makein: 'AUDI',
                    _product: 'Lease',
                    _vehicleType: 'Demo',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3',
                },
                {
                    _country: 'USA',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Lease',
                    _vehicleType: 'New',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3',
                },
                {
                    _country: 'USA',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Lease',
                    _vehicleType: 'CPO',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8',
                },
                {
                    _country: 'USA',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Lease',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6 7 8',
                },
                {
                    _country: 'USA',
                    _brand: 'VW',
                    _makein: 'VOLKSWAGEN',
                    _product: 'Lease',
                    _vehicleType: 'Demo',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3',
                },
                {
                    _country: 'USA',
                    _brand: 'Ducati',
                    _makein: 'DUCATI',
                    _product: 'Loan',
                    _vehicleType: 'New',
                    _relativeYears: '-4 -3 -2 -1 0 1 2 3',
                },
                {
                    _country: 'USA',
                    _brand: 'Ducati',
                    _makein: 'DUCATI',
                    _product: 'Loan',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5 6',
                },
                {
                    _country: 'USA',
                    _brand: 'Ducati',
                    _makenotin: 'DUCATI',
                    _product: 'Loan',
                    _vehicleType: 'PreOwned',
                    _relativeYears: '-1 0 1 2 3 4 5',
                },
            ],
        },
        statesRules: {
            stateRule: [
                {
                    _id: 'UsVwAudi',
                    _states: 'AK AR CA CO CT DE FL GA IL NY OK OR SC TN TX VA',
                },
                {
                    _id: 'UsBentley',
                    _states: 'IL NY RI TX',
                },
                {
                    _id: 'CanadaVwAudi',
                    _states: 'AB BC MB NB NL NS NT NU ON PE SK YT',
                },
                {
                    _id: 'UsDucati',
                    _states:
                        'AK AR AZ CA CT DE FL GA LA MA MN MS MO MT NE NJ NM NY ND OR RI SD TN VT WV WI',
                },
            ],
        },
    },
};

function getTermDate() {
    const termDate = sessionStorage.getItem(TERMDATE_ITEM_NAME);
    return termDate ? parseInt(termDate) : new Date().getFullYear();
}

export function getSubmittedByLabel(
    sourceSystem: string,
    submittedBy: string,
    isCanadian: boolean,
) {
    switch (sourceSystem) {
        case SourceSystem.DEALERTRACK:
        case SourceSystem.ONLINE:
            return submittedBy;
        case SourceSystem.AG:
            return 'AutoGravity';
        case SourceSystem.DMS:
            return `DMS-${submittedBy}`;
        case SourceSystem.OFA:
        case SourceSystem.ONLINECA:
            return isCanadian ? 'Consumer On-line' : 'Digital Online Finance';
        case SourceSystem.MNA:
            return 'My Next Audi';
        case SourceSystem.DARWIN:
        case SourceSystem.DIGITALRETAIL:
            return 'Tier 3 Online Finance';
        case SourceSystem.ROUTEONE:
            return 'Route One';
        default:
            return submittedBy;
    }
}
